import Analytics from '@/utils/analytics';
import React, { useCallback, useState } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { CachedImage } from '@usepepper/ui/atoms/cached-image';
import { ChatTime } from './chat-time';
import { createStyles } from '@/contexts/theme-context';
import { ImageViewerModal } from '@/components/image-viewer-modal';
import { Message } from './types';
import { MessageImageProps } from 'react-native-gifted-chat';
import { openWebBrowserURL } from '@/utils/navigation';
import { Platform, StyleSheet, TouchableOpacity, View } from 'react-native';
import { toPercentageString } from '@/utils/percentage';

type ChatMessageImageStatus = 'error' | 'loading' | 'loaded';

export const ChatMessageImage: React.FC<MessageImageProps<Message>> = (
  props,
) => {
  const [styles, theme] = useStyles();

  const [showImageViewer, setShowImageViewer] = useState(false);

  const hideImageViewer = useCallback(() => {
    setShowImageViewer(false);
  }, []);

  const [status, setStatus] = useState<ChatMessageImageStatus>('loading');
  const currentMessage = props.currentMessage as Message;
  const imageURL = currentMessage.image as string;
  const hasAttachedMessage = props.currentMessage?.text;

  return (
    <>
      <View style={styles.base}>
        {status === 'loading' ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator testID="image-loading-indicator" />
          </View>
        ) : null}
        <TouchableOpacity
          activeOpacity={0.8}
          disabled={status !== 'loaded'}
          onPress={() => {
            Analytics.track('chat_message.image.press');

            if (Platform.OS === 'web') {
              // Open images in a new tab
              openWebBrowserURL(imageURL);
            } else {
              setShowImageViewer(true);
            }
          }}
          // Cover the entire bubble
          style={StyleSheet.absoluteFillObject}
          testID="show-image-viewer-button"
        >
          <>
            <CachedImage
              alt="chat message image"
              contentFit="contain"
              onError={() => setStatus('error')}
              onLoad={() => setStatus('loaded')}
              source={{ uri: imageURL }}
              style={styles.image}
            />
            {!hasAttachedMessage ? (
              <View style={styles.timeContainer}>
                {
                  // `position` isn't defined in props but Bubble passes it in
                  // @ts-ignore
                  <ChatTime {...props} textColor={theme.colors.white} />
                }
              </View>
            ) : null}
          </>
        </TouchableOpacity>
        {status === 'error' ? (
          // TODO - display error state
          <View testID="error" />
        ) : null}
      </View>
      <ImageViewerModal
        imageURLs={[imageURL]}
        isVisible={showImageViewer}
        onClose={hideImageViewer}
      />
    </>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    backgroundColor: theme.colors.background,
    // 3:2 aspect ratio
    paddingBottom: toPercentageString((2 / 3) * 100),
  },
  image: {
    height: '100%',
    width: '100%',
  },
  loadingContainer: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
  },
  timeContainer: {
    bottom: 0,
    left: 0,
    paddingBottom: 10,
    paddingHorizontal: 14,
    position: 'absolute',
    right: 0,
  },
}));
